import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';

const AddressDetails = forwardRef(({ formData, setFormData }, ref) => {
    const [streetAddress, setStreetAddress] = useState(formData.streetAddress || '');
    const [landmark, setLandmark] = useState(formData.landmark || '');
    const [city, setCity] = useState(formData.city || '');
    const [state, setState] = useState(formData.state || '');
    const [pincode, setPincode] = useState(formData.postalCode || '');
    const [errors, setErrors] = useState({});

    const validate = () => {
        const errs = {};
        if (!streetAddress) errs.streetAddress = 'Street address is required.';
        if (!landmark) errs.landmark = 'Landmark is required.';
        if (!city) errs.city = 'City is required.';
        if (!state) errs.state = 'State is required.';
        if (!pincode) errs.pincode = 'Pincode is required.';
        else if (!/^\d{6}$/.test(pincode)) errs.pincode = 'Pincode must be a 6-digit number.';
        setErrors(errs);
        return errs;
    };

    useImperativeHandle(ref, () => ({
        validate
    }));

    return (
        <div>
            <Helmet>
                <title>Add Address</title>
            </Helmet>

            <div className="payout-setup">
                <div className='ws-cl-header'>
                    <div>
                        <h4 className='ws-title'>Add the address associated with this account</h4>
                        <p className='ws-desc'>This is the address the bank or financial institution has on file for this account. It should match recent bank statements.</p>
                    </div>
                </div>

                <div className="address-box">
                    <div className="po-form-box">
                        <div className='form-flex form-icon-box'>
                            <div className='form-icon'>
                                <img
                                    src='/assets/img/icons/pointer_icon.svg'
                                    className='form-icon img-fluid'
                                    alt=''
                                />
                            </div>
                            <div className='form-txt'>
                                <div className='form-floating'>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.streetAddress ? 'is-invalid' : ''}`}
                                        id="streetAddress"
                                        value={streetAddress}
                                        onChange={(e) => {
                                            setStreetAddress(e.target.value);
                                            setFormData(prev => ({ ...prev, streetAddress: e.target.value }));
                                            setErrors((prev) => ({ ...prev, streetAddress: '' }));
                                        }}
                                    />
                                    <label htmlFor="streetAddress">Street Address</label>
                                    {errors.streetAddress && <div className="invalid-feedback">{errors.streetAddress}</div>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="po-form-box">
                        <div className='form-flex form-icon-box'>
                            <div className='form-icon'>
                                <img
                                    src='/assets/img/icons/pointer_icon.svg'
                                    className='form-icon img-fluid'
                                    alt=''
                                />
                            </div>
                            <div className='form-txt'>
                                <div className='form-floating'>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.landmark ? 'is-invalid' : ''}`}
                                        id="landmark"
                                        value={landmark}
                                        onChange={(e) => {
                                            setLandmark(e.target.value);
                                            setFormData(prev => ({ ...prev, landmark: e.target.value }));
                                            setErrors((prev) => ({ ...prev, landmark: '' }));
                                        }}
                                    />
                                    <label htmlFor="landmark">Landmark</label>
                                    {errors.landmark && <span className="error-icon">
                                        <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                        <span className="tooltip-text">This field is required</span>
                                    </span>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="po-form-box">
                        <div className='form-flex form-icon-box'>
                            <div className='form-icon'>
                                <img
                                    src='/assets/img/icons/pointer_icon.svg'
                                    className='form-icon img-fluid'
                                    alt=''
                                />
                            </div>
                            <div className='form-txt'>
                                <div className='form-floating'>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                                        id="city"
                                        value={city}
                                        onChange={(e) => {
                                            setCity(e.target.value);
                                            setFormData(prev => ({ ...prev, city: e.target.value }));
                                            setErrors((prev) => ({ ...prev, city: '' }));
                                        }}
                                    />
                                    <label htmlFor="city">City</label>
                                    {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="po-form-box">
                        <div className='form-flex form-icon-box'>
                            <div className='form-icon'>
                                <img
                                    src='/assets/img/icons/pointer_icon.svg'
                                    className='form-icon img-fluid'
                                    alt=''
                                />
                            </div>
                            <div className='form-txt'>
                                <div className='form-floating'>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.state ? 'is-invalid' : ''}`}
                                        id="state"
                                        value={state}
                                        onChange={(e) => {
                                            setState(e.target.value);
                                            setFormData(prev => ({ ...prev, state: e.target.value }));
                                            setErrors((prev) => ({ ...prev, state: '' }));
                                        }}
                                    />
                                    <label htmlFor="state">State</label>
                                    {errors.state && <div className="invalid-feedback">{errors.state}</div>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="po-form-box">
                        <div className='form-flex form-icon-box'>
                            <div className='form-icon'>
                                <img
                                    src='/assets/img/icons/pointer_icon.svg'
                                    className='form-icon img-fluid'
                                    alt=''
                                />
                            </div>
                            <div className='form-txt'>
                                <div className='form-floating'>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.pincode ? 'is-invalid' : ''}`}
                                        id="pincode"
                                        value={pincode}
                                        onChange={(e) => {
                                            setPincode(e.target.value);
                                            setFormData(prev => ({ ...prev, postalCode: e.target.value }));
                                            setErrors((prev) => ({ ...prev, pincode: '' }));
                                        }}
                                    />
                                    <label htmlFor="pincode">Pincode</label>
                                    {errors.pincode && <span className="error-icon">
                                        <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                        <span className="tooltip-text">This field is required</span>
                                    </span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default AddressDetails;